/**
 * FastVi brand mark — pure SVG, resolution-independent, no raster assets.
 * Reusable at any size; designed to also seed future favicon/PWA icons.
 *
 * Composition:
 *   - Rounded-square ivory tile (iOS-icon radius), charcoal border with a
 *     subtle beveled edge (slightly darker bottom + lighter top highlight)
 *   - Wordmark: heavy grotesque "Fast" in near-black + "Vi" in brand orange,
 *     with a soft orange outer glow on "Vi".
 *
 * Pass `animatedGlow` to enable a one-shot CSS pulse on the "Vi" glow.
 */
export function FastViLogo({ size = 160, animatedGlow = false, className = "" }) {
  const uid = "fastvi"; // single-logo page is fine; avoid id collisions by scoping
  return (
    <svg
      viewBox="0 0 400 400"
      width={size}
      height={size}
      role="img"
      aria-label="FastVi"
      className={className}
      data-testid="fastvi-logo"
    >
      <defs>
        {/* Soft bevel on the tile */}
        <linearGradient id={`${uid}-face`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#FFFEFA" />
          <stop offset="55%" stopColor="#FAFAF7" />
          <stop offset="100%" stopColor="#EDEBE2" />
        </linearGradient>

        {/* Outer glow around the Vi */}
        <filter id={`${uid}-glow`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="6" result="blur" />
          <feFlood floodColor="#FF6B00" floodOpacity="0.55" />
          <feComposite in2="blur" operator="in" result="softGlow" />
          <feMerge>
            <feMergeNode in="softGlow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Shadow under the tile for depth */}
      <rect x="24" y="36" width="352" height="352" rx="76" ry="76"
            fill="#000" opacity="0.08" />

      {/* Tile face */}
      <rect
        x="18" y="18" width="364" height="364"
        rx="80" ry="80"
        fill={`url(#${uid}-face)`}
        stroke="#1A1A1A"
        strokeWidth="6"
      />
      {/* Inner highlight (top) + darker bottom edge for a 3D feel */}
      <path d="M 60 32 Q 200 20 340 32" stroke="#FFFFFF" strokeOpacity="0.9"
            strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M 60 368 Q 200 380 340 368" stroke="#0A0A0A" strokeOpacity="0.25"
            strokeWidth="3" fill="none" strokeLinecap="round" />

      {/* Wordmark */}
      <g textAnchor="middle" dominantBaseline="central"
         fontFamily="'Archivo Black', 'Inter', 'Helvetica Neue', system-ui, sans-serif"
         fontWeight="900" letterSpacing="-6">
        <text x="155" y="208" fontSize="128" fill="#111111">Fast</text>
        <text
          x="282" y="208" fontSize="128" fill="#FF6B00"
          filter={`url(#${uid}-glow)`}
          className={animatedGlow ? "fastvi-vi-pulse" : undefined}
        >
          Vi
        </text>
      </g>

      {animatedGlow && (
        <style>{`
          @keyframes fastvi-vi-pulse {
            0%   { opacity: 1; }
            35%  { opacity: 1; filter: url(#${uid}-glow) brightness(1.08); }
            70%  { opacity: 1; filter: url(#${uid}-glow) brightness(1); }
            100% { opacity: 1; }
          }
          .fastvi-vi-pulse { animation: fastvi-vi-pulse 1.2s ease-out 0.25s 1 both; }
          @media (prefers-reduced-motion: reduce) {
            .fastvi-vi-pulse { animation: none !important; }
          }
        `}</style>
      )}
    </svg>
  );
}

export default FastViLogo;
